// ─────────────────────────────────────────────────────────────────────────────
// RexyHomepage — the desktop JARVIS console homepage (Chunk 5 → CI-4 migrated).
//
// CI-4 migration (atomic with the AppShell mount in App.tsx — CORRECTION 2):
// the lifecycle ownership has been LIFTED to AppShell.
//   • useRexySocket / useMicMeter / useSpacebarPTT — gone (AppShell owns)
//   • telemetry-feed accumulator + warmth + active — gone (the signal RTT
//     probe is retired entirely: CONNECTION is server-sampled on the DGX)
//     (AppShell owns; exposed via useAppShellLive())
//   • self-contained <AtmosphereBackground/> — gone (AppShell renders it once,
//     across the whole authenticated tree)
//   • self-contained bottom <DomainBar/> — gone (AppShell renders it once)
//
// What remains: the three-panel layout (left vitals/telemetry, centre orb
// (itself the PTT tap target, no separate button per iOS parity), right
// radar / core-connection line / voice services — the Audio I/O panel moved
// whole to the SETTINGS page, wiring unchanged), the header pills, the orb-slot
// ResizeObserver (purely local layout). All live values arrive through
// useAppShellLive() - single owner, no double hooks (Flag 3).
//
// Root background is now transparent so AppShell's AtmosphereBackground bleeds
// through. The 0.5s fade-in keyframe is retained for the PIN-→-homepage feel.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useEffect, useRef, useState } from 'react';
import { REXY_FONT, REXY_CONSOLE } from '../lib/rexyConsole';
import { useAppShellLive } from './shell/AppShellContext';
import SystemVitalsCard from './SystemVitalsCard';
import VoiceServicesCard from './VoiceServicesCard';
import TelemetryCard from './TelemetryCard';
import ProximityRadarCanvas from './ProximityRadarCanvas';
import CoreConnectionCard from './CoreConnectionCard';
import RexyOrbThree from './RexyOrbThree';
import MuteButton from './shell/MuteButton';
import MutedNotice from './shell/MutedNotice';
import { DashboardLeft, DashboardRight } from './dashboard/DashboardColumns';
import ChatSurface, { ChatSurfaceProps } from './chat/ChatSurface';
import CornerOrb from './shell/CornerOrb';
import RexyGraphLab from './graph/dev/RexyGraphLab';

const MONO = REXY_FONT;
const PILLS = ['ONLINE', 'SECURE', 'ENCRYPTED', 'AUTH-LVL9'] as const;

function PillDot({ healthy }: { healthy: boolean }) {
  return (
    <span
      style={{
        width: 6,
        height: 6,
        borderRadius: 3,
        display: 'inline-block',
        backgroundColor: healthy ? REXY_CONSOLE.primary : REXY_CONSOLE.critical,
        animation: 'rexyPillPulse 2s ease-in-out infinite',
      }}
    />
  );
}

// CI-4: RexyHomepage takes no props. activeDomain/onSelectDomain are gone — the
// bottom DomainBar is now owned by AppShell, so the homepage no longer has any
// routing-level concerns of its own.
export default function RexyHomepage({ rexyChat }: { rexyChat: ChatSurfaceProps }) {
  // Single-owner consumption (Flag 3 + FIX 1).
  const {
    connected,
    vitals,
    feed,
    warmth,
    active,
    userAmplitude,
    agentAmplitude,
    ptt,
    muted,
    mutedStandingS,
  } = useAppShellLive();

  // ── Orb size — measured from its square centre slot (local layout) ──────────
  const orbSlotRef = useRef<HTMLDivElement | null>(null);
  const [orbSize, setOrbSize] = useState(0);

  // 6.7 — CONSOLE | DASHBOARD tab. Additive: the Console tab is the unchanged
  // landing page; toggling swaps ONLY the side columns, never the centre orb, so
  // the PTT sphere stays centre and the voice session is never lost on toggle.
  // GRAPH is a fourth tab that renders NOTHING. Provisional name — the owner rules
  // on the final label when the spatial graph lands; nothing here is that graph.
  const [homeTab, setHomeTab] = useState<'console' | 'dashboard' | 'chat' | 'graph'>('console');
  useEffect(() => {
    const el = orbSlotRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      setOrbSize(Math.floor(Math.min(width, height)));
    });
    ro.observe(el);
    return () => ro.disconnect();
    // P1a — depend on homeTab: when CHAT replaces the panels block the orbSlot node
    // unmounts (observer detaches, orbSize → 0); on return to a sphere-bearing tab a
    // NEW node mounts, so the effect must re-run to re-observe + re-measure it. With
    // `[]` it stayed bound to the detached node and the orb never re-rendered.
  }, [homeTab]);

  return (
    <div style={styles.root}>
      <style>{KEYFRAMES}</style>

      {/* Header */}
      <header style={styles.header}>
        <span style={styles.brand}>REXY</span>
        <div style={styles.pillRow}>
          {PILLS.map((label) => (
            <span key={label} style={styles.pill}>
              <PillDot healthy={label !== 'ONLINE' || connected} />
              <span style={styles.pillLabel}>{label}</span>
            </span>
          ))}
        </div>
      </header>

      {/* CONSOLE | DASHBOARD | CHAT | GRAPH tab switch — additive; swaps the body only. */}
      <div style={styles.tabRow}>
        {(['console', 'dashboard', 'chat', 'graph'] as const).map((t) => (
          <button
            key={t}
            type="button"
            role="tab"
            aria-selected={homeTab === t}
            onClick={() => setHomeTab(t)}
            style={{
              ...styles.tab,
              borderBottomColor: homeTab === t ? REXY_CONSOLE.primary : 'transparent',
              color: homeTab === t ? REXY_CONSOLE.primary : REXY_CONSOLE.mutedText,
            }}
          >
            {t === 'console'
              ? 'CONSOLE'
              : t === 'dashboard'
              ? 'DASHBOARD'
              : t === 'chat'
              ? 'CHAT'
              : 'GRAPH'}
          </button>
        ))}
      </div>

      {homeTab === 'graph' ? (
        /* GRAPH tab — the spatial graph. Its own branch, not a fall-through: the
           branching below is chat-versus-everything-else, so without this arm it
           would render the console/dashboard panel layout.

           The graph is mounted EMBEDDED, which makes it a flex child that takes
           the room left below the header and the tab switch and no more. It reads
           only synthetic `lab:` data — no backend call, no socket, no token, no
           registry or topology read — and moving it inside the authenticated tree
           does not change that.

           Nothing is added around it: no wrapper, no container, no heading, no
           panel, no border. The graph's own root IS the flex child. */
        <RexyGraphLab embedded />
      ) : homeTab === 'chat' ? (
        /* CHAT tab — the persistent {HOMEPAGE, REXY} thread, reusing the same
           ChatSurface every agent uses. Full-width; the centre orb yields to the
           reused CornerOrb (below), which keeps PTT initiable so the voice
           front-door persists. */
        <div style={styles.chatFull}>
          <ChatSurface {...rexyChat} />
        </div>
      ) : (
        /* CONSOLE / DASHBOARD — centre orb persists; only the asides swap. */
        <div style={styles.panels}>
          <aside style={styles.left}>
            {homeTab === 'console' ? (
              /* LEFT: System Vitals → Telemetry. Voice Services moved across to the
                 right column, so Telemetry moves up into the space it left. */
              <>
                <SystemVitalsCard vitals={vitals} />
                <TelemetryCard telemetry={feed} />
              </>
            ) : (
              <DashboardLeft />
            )}
          </aside>

          <main style={styles.center}>
            <div ref={orbSlotRef} style={styles.orbSlot} onMouseDown={ptt}>
              {orbSize > 0 && (
                <RexyOrbThree
                  size={orbSize}
                  userAmplitude={userAmplitude}
                  agentAmplitude={agentAmplitude}
                  warmth={warmth}
                  sessionActive={active}
                  // Console/Dashboard centre orb: the traced head at full
                  // detail while idle. sessionActive=true still hands straight
                  // back to the accepted orb, unchanged.
                  idleFormation="rexy"
                  silhouetteConfiguration="fixed"
                />
              )}
            </div>
            {/* Mute button — lower-left near the orb, matching iOS spatial intent.
                SIBLING of orbSlot (not a child) so its clicks don't bubble to the
                orb's PTT onMouseDown. Active-session-only (self-gated). */}
            <div style={styles.muteAnchor}>
              <MuteButton />
              {/* THE BUTTON WAS THE ONLY THING THAT CHANGED, and a swapped 32px
                  icon is not a statement (Fault 2, 2026-08-31). This says it in
                  words, for as long as it is true. */}
              <MutedNotice muted={muted} standingS={mutedStandingS} />
            </div>
          </main>

          <aside style={styles.right}>
            {homeTab === 'console' ? (
              /* RIGHT: Proximity → Core Connection as ONE LINE → Voice Services.
                 The Audio I/O panel no longer renders here; it moved WHOLE to the
                 SETTINGS page, wiring unchanged. Nothing was deleted — these are the
                 same components, mounted in a different place. */
              <>
                <ProximityRadarCanvas />
                <CoreConnectionCard variant="line" />
                <VoiceServicesCard services={vitals?.voice_services || null} />
              </>
            ) : (
              <DashboardRight />
            )}
          </aside>
        </div>
      )}

      {/* CHAT tab — persistent PTT front-door. CornerOrb now owns its own click
          surface (S4 drag wrapper), so the separate transparent hit-layer is
          gone: exactly ONE call handler, and a drag can never be a tap. */}
      {homeTab === 'chat' && <CornerOrb />}

      {/* (Bottom DomainBar removed — AppShell renders it once across the tree.) */}
    </div>
  );
}

const KEYFRAMES = `
@keyframes rexyPillPulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
}
@keyframes rexyHomeFadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}`;

const styles: Record<string, React.CSSProperties> = {
  root: {
    position: 'relative',
    width: '100%',
    height: '100%',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    color: REXY_CONSOLE.primary,
    fontFamily: MONO,
    // CI-4: transparent so AppShell's AtmosphereBackground shows through.
    background: 'transparent',
    // Smooth fade up on PIN→homepage transition; never a pop-in.
    animation: 'rexyHomeFadeIn 0.5s ease-out',
  },
  header: {
    position: 'relative',
    zIndex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 18px',
    borderBottom: `1px solid ${REXY_CONSOLE.divider}`,
  },
  brand: {
    fontFamily: MONO,
    fontSize: 22,
    fontWeight: 700,
    letterSpacing: 5,
    color: REXY_CONSOLE.primary,
  },
  pillRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
  },
  tabRow: {
    position: 'relative',
    zIndex: 1,
    display: 'flex',
    gap: 24,
    padding: '0 18px',
    borderBottom: `1px solid ${REXY_CONSOLE.divider}`,
  },
  tab: {
    background: 'transparent',
    border: 'none',
    borderBottom: '2px solid transparent',
    padding: '10px 0',
    fontFamily: MONO,
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 2,
    cursor: 'pointer',
    transition: 'color 200ms ease, border-color 200ms ease',
  },
  pill: {
    display: 'flex',
    alignItems: 'center',
    gap: 5,
  },
  pillLabel: {
    fontFamily: MONO,
    fontSize: 9,
    letterSpacing: 1.5,
    color: REXY_CONSOLE.primary,
  },
  panels: {
    position: 'relative',
    zIndex: 1,
    flex: 1,
    minHeight: 0,
    display: 'flex',
    gap: 14,
    padding: 14,
    boxSizing: 'border-box',
  },
  chatFull: {
    position: 'relative',
    zIndex: 1,
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  left: {
    width: 340,
    flexShrink: 0,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
    overflowY: 'auto',
    // Overflow logic is deliberately UNTOUCHED (owner correction 2026-07-24): content
    // fitting stands on the wrapping + flex-shrink rules alone — `minWidth: 0` on the
    // ellipsising cells was the actual root cause. No overflowX clamp here.
  },
  center: {
    position: 'relative',   // anchor for the absolutely-positioned mute button
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 16,
  },
  muteAnchor: {
    position: 'absolute',
    left: 24,
    bottom: 24,
    zIndex: 2,
  },
  orbSlot: {
    flex: 1,
    width: '100%',
    minHeight: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    userSelect: 'none',
  },
  right: {
    width: 300,
    flexShrink: 0,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
    overflowY: 'auto',
  },
};
